// /admin/v1/backends — GPU 算力池 (gpu-backend-pool).
//
// 平台级跨 App 共享的 Onething backend 池: 注册多台 GPU backend, 健康检查
// 探活 + 负载计数, 故障转移调度. 一台关机自动绕开, 解决 502 单点.

import { get, post, put, del } from './client';

export type BackendStatus = 'online' | 'offline' | 'idle' | 'maintenance';

// EngineReadinessEntry is the per-engine calibration result.
// engine-calibration capability.
export interface EngineReadinessEntry {
  ready: boolean;
  missing?: string[];
  smoke_at?: string | null; // RFC3339 timestamp of last successful smoke-test
  reason?: string;
}

// EngineReadinessMap is the engine_readiness JSONB column value.
// Keys: "reactor" | "reactor_multi" | "pulid_reactor" | "jimeng"
export type EngineReadinessMap = Record<string, EngineReadinessEntry>;

export interface Backend {
  ext_id: string; // bk_* ULID, 用户可见 ID
  name: string;
  base_url: string;
  status: BackendStatus;
  running_count: number;
  pending_count: number;
  last_heartbeat_at?: string | null;
  priority: number;
  created_at: string;
  // 关联的 OneThingAI 实例 appId (onething-instance-control). 空 → 纯手填 URL,
  // 不支持实例级 boot/shutdown 操作.
  onething_app_id?: string;
  // engine_readiness 引擎就绪矩阵 (engine-calibration). 空 = 未探测.
  engine_readiness?: EngineReadinessMap;
  // gpu-availability-view: 该 backend 对应 GPU 类型当前是否有卡可用.
  // true = 有卡在跑/可用, false = 不可用, null/undefined = 未知 (token 未配/查询失败).
  gpu_available?: boolean | null;
  // SSH host (gpu-backend-install-on-wake). 非空 = 已配 SSH → install-on-wake 可用.
  // 密码/host_key 不下发 (json:"-").
  ssh_host?: string;
}

export interface BackendSummary {
  total: number;
  online: number;
  offline: number;
  idle: number;
  maintenance: number;
  pending_total: number;
}

export interface ListBackendsResult {
  backends: Backend[];
  summary: BackendSummary;
}

export interface CreateBackendInput {
  name: string;
  base_url: string;
  api_key: string;
  // 可选: 关联 OneThingAI 实例 appId. "从 OneThingAI 添加实例" 时自动带上.
  onething_app_id?: string;
}

// OneThingAI 实例状态码 (见 onething-instance-control spec):
// 100=部署 200=启动 300=运行 400=关机中 800=停止.
export type OnethingInstanceStatus = 100 | 200 | 300 | 400 | 800 | number;

export interface OnethingInstance {
  app_id: string;
  status: OnethingInstanceStatus;
  gpu_type: string;
  web_ui: string;
}

export interface ListOnethingInstancesResult {
  // 后端在 token 未配时返回 configured:false + 空 instances (200, 不抛错 —
  // 符合后端错误码铁律, 不自造业务码). UI 据此置灰开关/添加按钮.
  configured: boolean;
  instances: OnethingInstance[];
}

export function listBackends(): Promise<ListBackendsResult> {
  return get<ListBackendsResult>('/admin/v1/backends');
}

export function createBackend(input: CreateBackendInput): Promise<Backend> {
  return post<Backend>('/admin/v1/backends', input);
}

export function deleteBackend(extId: string): Promise<void> {
  return del<void>(`/admin/v1/backends/${extId}`);
}

/** SSH 连接信息 (gpu-backend-install-on-wake). 全替换; 设置即触发首连钉扎 host key. */
export interface UpdateBackendSSHInput {
  ssh_host: string;
  ssh_port: number;
  ssh_user: string;
  ssh_password: string;
}

/** 配置/更新该 backend 的 SSH 信息 (install-on-wake 自动装节点链用). 密码加密入库. */
export function updateBackendSSH(extId: string, input: UpdateBackendSSHInput): Promise<void> {
  return put<void>(`/admin/v1/backends/${extId}/ssh`, input);
}

/** 开机: 对该 backend 关联的 OneThingAI 实例 boot. 未关联/未配置 → 错误. */
export function bootBackend(extId: string): Promise<void> {
  return put<void>(`/admin/v1/backends/${extId}/boot`);
}

/** 关机: 对该 backend 关联的 OneThingAI 实例 shutdown (停止计费). */
export function shutdownBackend(extId: string): Promise<void> {
  return put<void>(`/admin/v1/backends/${extId}/shutdown`);
}

/** 列 OneThingAI 全部实例 (供 "从 OneThingAI 添加实例"). token 未配 → 错误. */
export function listOnethingInstances(): Promise<ListOnethingInstancesResult> {
  return get<ListOnethingInstancesResult>('/admin/v1/onething/instances');
}

// ---------------------------------------------------------------------------
// gpu-pool-admin-controls: 自动扩缩配置 (autoscale)
// ---------------------------------------------------------------------------

// AutoscaleConfig 是 GET/PUT /admin/v1/backends/autoscale 的读写结构.
export interface AutoscaleConfig {
  // 总开关: 关闭后不自动开/关机, 完全人工.
  enabled: boolean;
  // 人均待执行任务数超过该阈值才自动开机 (≥1).
  scale_up_pending: number;
  // 实例闲置满 N 秒自动关机省钱 (≥60).
  idle_shutdown_after_sec: number;
  // 常驻最少在线台数, >0 会持续计费 (≥0).
  min_online: number;
  // 仅 GET 时回带 (只读).
  updated_at?: string;
}

export function getAutoscaleConfig(): Promise<AutoscaleConfig> {
  return get<AutoscaleConfig>('/admin/v1/backends/autoscale');
}

export function putAutoscaleConfig(input: AutoscaleConfig): Promise<AutoscaleConfig> {
  return put<AutoscaleConfig>('/admin/v1/backends/autoscale', {
    enabled: input.enabled,
    scale_up_pending: input.scale_up_pending,
    idle_shutdown_after_sec: input.idle_shutdown_after_sec,
    min_online: input.min_online,
  });
}
